'use strict';

import * as Onyx from '../lib/onyx.min.js';

const STEER_SPEED = 0.1;
const TOP_SPEED = 1.8;
const ACCELERATION = 0.040;
const DECELERATION = 0.025;
const DEST_TOLERANCE = 40;
const MAX_AUDIO_DISTANCE = 25;
const ONLY_PLAYERS_ENGINE_AUDIO = true;
const HANDLING = 25; // Lower is better -- 1 is perfect
const HANDLING_SPEED = 0.125; // The maximum it can change handling per frame
const ROLL_RATIO = 1.25;
const BOOST_AMOUNT = 1.0;
const BOOST_HANDLING = 13; // Lower is better -- 1 is perfect
const BOOST_TIME = 975;
const BURNOUT_AMOUNT = -1.4;
const BURNOUT_HANDLING = 35;
const BURNOUT_TIME = 1000;

export default class Player extends Onyx.Entity {
    constructor({isPlayer = false, materialNo = 1, startingNURBS = 0, startingOffset = 0, name = ""} = {}){
        super(...arguments);

        this.isPlayer = isPlayer;
        this.name = name;

        this.acceleration = 0;
        this.velocity = 0;
        this.angularVelocity = 0;
        this.topSpeedConst = TOP_SPEED;
        this.topSpeed = TOP_SPEED;
        this.handling = HANDLING;
        this.currentOffset = startingOffset;
        this.currentHandling = HANDLING;

        this.engineSFXRateOffset = Math.random() * 0.05; // This is so the audio waves don't cancel each other out

        this.currentNURBS = null;
        this.currentNURBSIndex = startingNURBS;
        this.laps = -1;

        this.accelerate = false;
        this.steer = 0;

        this.isBoosting = false;

        this.materialNo = materialNo;
        
        Onyx.ImportModelFromOBJ.load({src: "res/models/ship_1.obj"}).then((model) => {
            model.transform.yaw = -Math.PI / 2;
            // model.transform.roll = -Math.PI / 2;
            this.addComponent(model);

            this.swapMaterials(this.materialNo);

            // Move up and down
            this.parentScene.setModulate((scene, dt, progress) => {
                var v = 0 + (Math.PI-0)*((-Math.cos(progress*Math.PI)/2) + 0);
                this.components[0].transform.y += (-v / 300);
                this.components[1].transform.sx -= (-v / 500);
                this.components[1].transform.sy -= (-v / 500);
                this.components[2].transform.y += (-v / 300);
            }, 400 + (Math.random() * 200));

            // Shadow
            this.shadow = new Onyx.Sprite({
                id: "shadow", 
                src: "res/textures/rrr_vehicleSHADOW_001.png", 
                pixelsPerUnit: 0.0285, 
                pivot: [0.5, 0.5],
                transform: new Onyx.Transform({
                    position: [0, -4.95, 0],
                    rotation: [-Math.PI / 2, 0, 0]
                })
            });
            this.addComponent(this.shadow);

            Onyx.ImportModelFromOBJ.load({src: "res/models/burner.obj"}).then((model) => {
                model.transform.yaw = -Math.PI / 2;
                model.meshes[0].material.lighting = 0;
                model.meshes[1].material.lighting = 0;
                this.addComponent(model);
            });
        });

        this.engineSound = game.sfx["engine"];
        this.engineSound.loop(true);
    }

    onSpawn(scene){
        super.onSpawn(...arguments);
        if(this.isPlayer) scene.player = this;
        scene.players.push(this);

        this.getNextNURBS(this.currentNURBSIndex);

        this.destination = this.currentNURBS;
        this.destinationFar = this.nextNURBS;

        // Find mid point
        let d = Onyx.vec3.distance(this.destination, this.transform.xyz) - DEST_TOLERANCE;
        let d2 = Onyx.vec3.distance(this.destination, this.destinationFar);
        let t = d / d2;
        t = Math.max(t, 0);
        t = Math.min(t, 1);
        this.destinationMid = [];
        Onyx.vec3.lerp(this.destinationMid, this.destination, this.destinationFar, 1 - t);

        // Offset mid point
        let offset = [this.currentOffset, 0, 0];
        let a = Math.atan2(this.destination[0] - this.destinationFar[0], this.destination[2] - this.destinationFar[2]); // Get angle
        Onyx.vec3.rotateY(offset, offset, [0, 0, 0], a) - Math.PI; // Rotate offset around A
        Onyx.vec3.add(this.destinationMid, this.destinationMid, offset); // Add offset to midpoint

        this.transform.x = this.destinationMid[0];
        this.transform.y = this.destinationMid[1];
        this.transform.z = this.destinationMid[2];

        // this.getNURBS(1);

        this.wobbleModulator = this.parentScene.setModulate((scene, dt, progress) => {
            let emit = progress / 16 + 0.6375; // Flicker
            emit += (this.velocity / TOP_SPEED) * 0.3; // relative to speed

            if(this.components[0]) {
                this.components[0].meshes[2].material.emitExponent = emit;
                this.components[0].meshes[6].material.emitExponent = emit;
                this.components[0].meshes[3].material.emitExponent = emit;
                this.components[0].meshes[4].material.emitExponent = emit;
                this.components[0].meshes[5].material.emitExponent = emit;
            }

            // Burners
            if(this.components[2]){
                this.components[2].meshes[0].material.emitExponent = emit;
                this.components[2].meshes[1].material.emitExponent = emit;
            }
        }, 64);

        this.engineSound.once('play', () => {
            this.engineSound.pannerAttr({
                panningModel: 'HRTF',
                distanceModel: 'inverse',
                coneInnerAngle: 360,
                coneOuterAngle: 360,
                coneOuterGain: 0,
                refDistance: 18,
                maxDistance: 68,
                rolloffFactor: 1
            }, this.engineSoundId);
        });
    }

    update(...args){
        super.update(...args);

        this.currentOffset += this.steer * STEER_SPEED;
        this.currentOffset = Math.min(this.parentScene.currentLevel.laneWidth, this.currentOffset);
        this.currentOffset = Math.max(-this.parentScene.currentLevel.laneWidth, this.currentOffset);

        // Set the position/rate/volume of the engine sound in 3D space.
        if((ONLY_PLAYERS_ENGINE_AUDIO && this.isPlayer) || Math.abs(Onyx.vec3.distance(this.transform.xyz, this.parentScene.cameras[0].transform.xyz) < MAX_AUDIO_DISTANCE)){
            if(!this.engineSoundId) this.engineSoundId = this.engineSound.play();

            this.engineSound.pos(this.transform.x, this.transform.y, this.transform.z, this.engineSoundId);
            this.engineSound.volume(0.5 + (0.5 * (this.velocity / TOP_SPEED)), this.engineSoundId);
            // Performance Optimization: Rate takes some CPU, so lets only update if necessary
            if(this.engineSoundRate !== 0.3 + (0.7 * (this.velocity / TOP_SPEED)) + this.engineSFXRateOffset){
                this.engineSoundRate = 0.3 + (0.7 * (this.velocity / TOP_SPEED)) + this.engineSFXRateOffset;
                this.engineSound.rate(this.engineSoundRate, this.engineSoundId);
            }
        }else if(this.engineSoundId){
            this.engineSound.pause(this.engineSoundId);
            this.engineSoundId = null;
        }
        

        if(this.accelerate && this.velocity < this.topSpeed){
            this.velocity += ACCELERATION;
        }else if(Math.abs(this.velocity - this.topSpeed) < .02){
            // Maintain speed
        }else{
            this.velocity -= DECELERATION;
            if(this.velocity < 0) this.velocity = 0;
        }

        // Do the same with handling, to smooth out the handling transitions
        if(this.currentHandling < this.handling){
            this.currentHandling += HANDLING_SPEED;
        }else if(Math.abs(this.currentHandling - this.handling) < 1){
            // Maintain
        }else{
            this.currentHandling -= HANDLING_SPEED;
            if(this.currentHandling < 1) this.currentHandling = 1;
        }

        // this.transform.targetTo([bezierAhead[0], bezierAhead[1], bezierAhead[2]]);

        // Move towards chosen sub NURBS
        this.destination = this.currentNURBS;
        this.destinationFar = this.nextNURBS;

        // If within range of the destination, choose next sub NURBS
        while(Math.abs(Onyx.vec3.distance(this.destination, this.transform.xyz) < DEST_TOLERANCE)){
            this.getNextNURBS();

            this.destination = this.currentNURBS;
            this.destinationFar = this.nextNURBS;
        }

        // Find mid point
        let d = Onyx.vec3.distance(this.destination, this.transform.xyz) - DEST_TOLERANCE;
        let d2 = Onyx.vec3.distance(this.destination, this.destinationFar);
        let t = d / d2;
        t = Math.max(t, 0);
        t = Math.min(t, 1);
        this.destinationMid = [];
        Onyx.vec3.lerp(this.destinationMid, this.destination, this.destinationFar, 1 - t);

        // Offset mid point
        let offset = [this.currentOffset, 0, 0];
        let a = Math.atan2(this.destination[0] - this.destinationFar[0], this.destination[2] - this.destinationFar[2]); // Get angle
        Onyx.vec3.rotateY(offset, offset, [0, 0, 0], a) - Math.PI; // Rotate offset around A
        Onyx.vec3.add(this.destinationMid, this.destinationMid, offset); // Add offset to midpoint
        
        // Point towards next destination
        this.destinationAngle = Math.atan2(this.destinationMid[0] - this.transform.x, this.destinationMid[2] - this.transform.z) + Math.PI;
        // this.destinationAngleFar = Math.atan2(this.destinationFar[0] - this.transform.x, this.destinationFar[2] - this.transform.z) + Math.PI;

        let s = this.shortestDistDegrees(this.angularVelocity, this.destinationAngle);
        
        this.angularVelocity += s / this.currentHandling;
        this.transform.yaw = this.destinationAngle;
        this.components[0].transform.roll = (s / ROLL_RATIO);
        // this.components[0].transform.pitch = (-s / 2);
        // this.transform.roll = (Math.sin(-s) - (Math.PI / 2)) / 16;
        // this.transform.pitch = (Math.cos(-s) - (Math.PI / 2)) / 16;
        
        this.transform.x += this.velocity * Math.sin(this.angularVelocity + Math.PI);
        // this.transform.y += this.velocity * Math.sin(this.angularVelocity + Math.PI);
        this.transform.z += this.velocity * Math.cos(this.angularVelocity + Math.PI);

        this.components[0].meshes[2].transform.pitch =  (this.velocity / TOP_SPEED) * 0.1;
        this.components[0].meshes[6].transform.pitch = -(this.velocity / TOP_SPEED) * 0.1;
        // this.components[0].meshes[1].transform.yaw = this.destinationAngleFar;// - this.transform.yaw;

        // Burners
        this.components[2].transform.y = this.components[0].transform.y;
        this.components[2].transform.roll = (s / ROLL_RATIO);
        this.components[2].meshes[0].transform.pitch =  (this.velocity / TOP_SPEED) * 0.1;
        this.components[2].meshes[1].transform.pitch = -(this.velocity / TOP_SPEED) * 0.1;
        this.components[2].meshes[0].transform.sx = Math.max(this.velocity - TOP_SPEED / BOOST_AMOUNT, 0.0);
        this.components[2].meshes[1].transform.sx = Math.max(this.velocity - TOP_SPEED / BOOST_AMOUNT, 0.0);

        this.checkCollisions();

        // Update Debug Cubes
        if(this.isPlayer){
            this.parentScene.debugCubes[0].transform.xyz = this.destination;
            this.parentScene.debugCubes[1].transform.xyz = this.destinationFar;
            this.parentScene.debugCubes[2].transform.xyz = this.destinationMid;
        }
    }

    getNextNURBS(index = (this.currentNURBSIndex++) + 1, nextIndex = index + 1){
        index = index % (this.parentScene.subNURBS.length - 1);
        if(index < 0) index = (this.parentScene.subNURBS.length - 1) - -index;

        nextIndex = nextIndex % (this.parentScene.subNURBS.length - 1);
        if(nextIndex < 0) nextIndex = (this.parentScene.subNURBS.length - 1) - -nextIndex;

        if(index < this.currentNURBSIndex) this.laps++;
        
        this.currentNURBSIndex = index;
        this.currentNURBS = this.parentScene.subNURBS[index];
        this.nextNURBS = this.parentScene.subNURBS[nextIndex];
    }

    shortestDistDegrees(start, stop) {    
        const PI1 = Math.PI;  
        const PI2 = Math.PI * 2;
        const modDiff = (stop - start) % PI2;
        let shortestDistance = PI1 - Math.abs(Math.abs(modDiff) - PI1);
        return (modDiff + PI2) % PI2 < PI1 ? shortestDistance *= 1 : shortestDistance *= -1;
    }

    checkCollisions(){
        if(this.isLeaping) return;

        for(let player of this.parentScene.players){
            if(player !== this){
                if(!player.isLeaping
                    && !player.isBoosting
                    && player.currentNURBSIndex > this.currentNURBSIndex // Only affect the player in front
                    && Math.abs(player.transform.x - this.transform.x) < 5
                    && Math.abs(player.transform.z - this.transform.z) < 5){ 

                    // Pop into the air
                    player.isLeaping = true;
                    player.accelerate = false;

                    player.wobbleModulator.enabled = false;

                    player.velocity /= 2;

                    // Up
                    let t = new Onyx.Tween();
                    t.easeIn(this.components[0].transform, "y", this.components[0].transform.y + 10, 200, () => {
                        player.removeComponent(t);

                        // Back down
                        let t = new Onyx.Tween();
                        t.easeIn(this.components[0].transform, "y", this.components[0].transform.y - 10, 200, () => {
                            player.removeComponent(t);

                            player.isLeaping = false;
                            player.accelerate = true;
                        });
                        player.addComponent(t);
                    });
                    player.addComponent(t);
                }
            }
        }
    }

    boost(){
        this.isBoosting = true;
        this.topSpeed += BOOST_AMOUNT;
        this.handling = BOOST_HANDLING;

        this.parentScene.setTimeout(() => {
            this.isBoosting = false;
            this.topSpeed -= BOOST_AMOUNT;
            this.handling = HANDLING;
        }, BOOST_TIME);
    }

    burnout(){
        this.topSpeed += BURNOUT_AMOUNT;
        this.handling = BURNOUT_HANDLING;

        // TODO: Cleanup Tweening
        // Ship
        let t = new Onyx.Tween();
        t.easeIn(this.components[0].transform, "yaw", this.components[0].transform.yaw - (Math.PI * 2), BURNOUT_TIME, () => {
            this.removeComponent(t);

            this.components[0].transform.yaw = -Math.PI / 2;
        });
        t.from = -Math.PI / 2;
        t.to = (-Math.PI / 2) + (Math.PI * 2);
        this.addComponent(t);

        // Shadow
        // let tS = new Onyx.Tween();
        // tS.easeIn(this.components[1].transform, "yaw", this.components[1].transform.yaw - (Math.PI * 2), BURNOUT_TIME, () => {
        //     this.removeComponent(t);

        //     this.components[1].transform.yaw = -Math.PI / 2;
        // });
        // tS.from = -Math.PI / 2;
        // tS.to = (-Math.PI / 2) + (Math.PI * 2);
        // this.addComponent(tS);

        // Burners
        let tB = new Onyx.Tween();
        tB.easeIn(this.components[2].transform, "yaw", this.components[2].transform.yaw - (Math.PI * 2), BURNOUT_TIME, () => {
            this.removeComponent(t);

            this.components[2].transform.yaw = -Math.PI / 2;
        });
        tB.from = -Math.PI / 2;
        tB.to = (-Math.PI / 2) + (Math.PI * 2);
        this.addComponent(tB);

        this.parentScene.setTimeout(() => {
            this.topSpeed -= BURNOUT_AMOUNT;
            this.handling = HANDLING;
        }, BURNOUT_TIME);

        game.sfx['breakdown'].play();
    }

    swapMaterials(materialNo){
        if(!this.components[0]) return; // No model loaded yet

        Onyx.ImportMaterialsFromMTL.load({src: "res/models/ship_" + (materialNo + 1) + ".mtl"}).then((materials) => {
            for(let mesh of this.components[0].meshes){
                for(let material of materials){
                    if(material.name == mesh.material.name) mesh.material = material;
                }
            }
        });
    }
}